/**
 * ReportsManagement — admin review/triage of ALL user reports across the
 * platform (cross-community). Real /admin/reports endpoints; the previous
 * version was a hardcoded FE stub. List + status/reason filters + summary
 * counts + resolve action + CSV/PDF export.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileWarning, Download, FileText, ChevronLeft, ChevronRight, Filter, X, CheckCircle } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { adminService, type ReportRow } from '@/services/adminService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

const STATUSES = ['PENDING', 'REVIEWED', 'RESOLVED', 'DISMISSED'] as const
const REASONS = ['SPAM', 'HARASSMENT', 'MISINFORMATION', 'OFF_TOPIC', 'OTHER'] as const

const statusStyles: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  REVIEWED: 'bg-blue-100 text-blue-700',
  RESOLVED: 'bg-green-100 text-green-700',
  DISMISSED: 'bg-gray-100 text-gray-700',
}

const reasonStyles: Record<string, string> = {
  SPAM: 'bg-red-100 text-red-700',
  HARASSMENT: 'bg-orange-100 text-orange-700',
  MISINFORMATION: 'bg-amber-100 text-amber-700',
  OFF_TOPIC: 'bg-slate-100 text-slate-700',
  OTHER: 'bg-gray-100 text-gray-700',
}

const ResolveModal: React.FC<{
  report: ReportRow | null
  onClose: () => void
  onResolved: () => void
}> = ({ report, onClose, onResolved }) => {
  const [status, setStatus] = useState('RESOLVED')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (report) {
      setStatus('RESOLVED')
      setNote('')
    }
  }, [report])

  if (!report) return null

  const submit = async () => {
    setSaving(true)
    try {
      await adminService.resolveReport(report.id, { status, resolutionNote: note || undefined })
      onResolved()
      onClose()
    } catch (e) {
      console.error('resolve report', e)
      alert('Failed to resolve report.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Resolve Report #{report.id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm text-gray-600">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${reasonStyles[report.reason] || reasonStyles.OTHER}`}>{report.reason}</span>
            <span className="ml-2">by {report.reporter?.fullName || 'Unknown'}</span>
          </div>
          <div className="text-xs text-gray-500">
            Target: {report.reelId ? `Reel #${report.reelId}` : report.postId ? `Post #${report.postId}` : report.commentId ? `Comment #${report.commentId}` : '—'}
          </div>
          {report.details && <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{report.details}</p>}
          <div>
            <label className="text-xs font-medium text-gray-500">New Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Resolution Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full mt-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add a note about this resolution…"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
          <GovButton variant="secondary" size="sm" onClick={onClose}>Cancel</GovButton>
          <GovButton variant="primary" size="sm" onClick={submit} disabled={saving}>
            <CheckCircle className="w-4 h-4 inline mr-1" /> {saving ? 'Saving…' : 'Resolve'}
          </GovButton>
        </div>
      </div>
    </div>
  )
}

const ReportsManagement: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<ReportRow[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterReason, setFilterReason] = useState('')
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [resolveTarget, setResolveTarget] = useState<ReportRow | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login')
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') navigate('/feed')
  }, [user, isAuthenticated, navigate])

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const [data, c] = await Promise.all([
        adminService.getAdminReports(filterStatus, filterReason, page, 20),
        adminService.getAdminReportCounts().catch(() => ({})),
      ])
      setRows(data.content)
      setTotalPages(data.totalPages)
      setTotalElements(data.totalElements)
      setCounts(c)
    } catch (e) {
      console.error('fetch reports', e)
    } finally {
      setLoading(false)
    }
  }, [filterStatus, filterReason, page, user])

  useEffect(() => { fetch() }, [fetch])

  const changeStatus = (v: string) => { setFilterStatus(v); setPage(0) }
  const changeReason = (v: string) => { setFilterReason(v); setPage(0) }
  const clearFilters = () => { setFilterStatus(''); setFilterReason(''); setPage(0) }
  const hasFilters = !!(filterStatus || filterReason)

  // Human label for a report's target (used when there are no details). Post +
  // comment + reel reports are all possible; reel/comment ids fall back to "Post".
  const targetLabel = (r: ReportRow) => {
    if (r.reelId) return `Reel #${r.reelId}`
    if (r.postId) return `Post #${r.postId}`
    if (r.commentId) return `Comment #${r.commentId}`
    return '—'
  }

  const exportColumns: ExportColumn<ReportRow>[] = [
    { header: 'ID', key: 'id' },
    { header: 'Reason', key: 'reason' },
    { header: 'Status', key: 'status' },
    { header: 'Reporter', key: 'reporter', format: (v) => (v ? `${(v as any).fullName}` : '—') },
    { header: 'Post ID', key: 'postId', format: (v) => (v ? String(v) : '—') },
    { header: 'Comment ID', key: 'commentId', format: (v) => (v ? String(v) : '—') },
    { header: 'Reel ID', key: 'reelId', format: (v) => (v ? String(v) : '—') },
    { header: 'Details', key: 'details', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 200) : '') },
    { header: 'Resolved By', key: 'resolvedBy', format: (v) => (v ? `${(v as any).fullName}` : '—') },
    { header: 'Resolution Note', key: 'resolutionNote', format: (v) => (v ? String(v) : '') },
    { header: 'Created', key: 'createdAt', format: (v) => new Date(v as string).toLocaleString() },
  ]

  const cards = [
    { label: 'Total Reports', value: counts.TOTAL || 0, tone: 'text-gray-900' },
    { label: 'Pending', value: counts.PENDING || 0, tone: 'text-yellow-600' },
    { label: 'Reviewed', value: counts.REVIEWED || 0, tone: 'text-blue-600' },
    { label: 'Resolved', value: counts.RESOLVED || 0, tone: 'text-green-600' },
    { label: 'Dismissed', value: counts.DISMISSED || 0, tone: 'text-gray-500' },
  ]

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Reports Management" subtitle="Review and manage user reports across the platform" icon={FileWarning} />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {cards.map((c) => (
            <GovCard key={c.label}>
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className={`text-2xl font-bold ${c.tone}`}>{c.value.toLocaleString()}</p>
            </GovCard>
          ))}
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={filterStatus}
                onChange={(e) => changeStatus(e.target.value)}
                className="pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
              >
                <option value="">All Statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={filterReason}
                onChange={(e) => changeReason(e.target.value)}
                className="pl-10 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
              >
                <option value="">All Reasons</option>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1 px-3 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 rounded-lg">
                <X className="w-4 h-4" /> Clear
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('reports', exportColumns, rows)} disabled={!rows.length}>
                <Download className="w-4 h-4 inline mr-1" /> CSV
              </GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('reports', 'Reports Report', exportColumns, rows)} disabled={!rows.length}>
                <FileText className="w-4 h-4 inline mr-1" /> PDF
              </GovButton>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 p-8 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <GovEmptyState icon="📋" title="No Reports" description={hasFilters ? 'Try adjusting your filters.' : 'There are no reports to review at this time.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Reason</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Content</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Reporter</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Date</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${reasonStyles[r.reason] || reasonStyles.OTHER}`}>{r.reason}</span>
                      </td>
                      <td className="p-4 text-sm text-gray-700 max-w-xs">
                        {r.details ? <span className="block truncate">{r.details}</span> : <span className="text-gray-400">{targetLabel(r)}</span>}
                        {r.postId && <span className="block text-xs text-gray-400">Post #{r.postId}</span>}
                        {r.reelId && <span className="block text-xs text-gray-400">Reel #{r.reelId}</span>}
                      </td>
                      <td className="p-4 text-sm text-gray-700">{r.reporter?.fullName || '—'}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[r.status] || statusStyles.PENDING}`}>{r.status}</span>
                      </td>
                      <td className="p-4 text-sm text-gray-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setResolveTarget(r)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Resolve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </GovButton>
                <span className="text-sm text-gray-600">Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} total</span>
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                  Next <ChevronRight className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          )}
        </GovCard>
      </div>
      <ResolveModal report={resolveTarget} onClose={() => setResolveTarget(null)} onResolved={fetch} />
    </GovLayout>
  )
}

export default ReportsManagement